import {useMatomo} from '@jonkoops/matomo-tracker-react';
import classNames from 'classnames';
import Link from 'next/link';
import React, {ReactElement, useEffect, useMemo, useRef} from 'react';

import {CelebrationIcon, LossIcon, MoneyIcon, ProductionIcon, RestartIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import {useEnvironment} from '../../common/EnvironmentProvider';
import Row from '../../common/Row';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {useAppDispatch} from '../../store';
import {formatNumber} from '../../tool/decision/util';
import {Actions} from '../gameSlice';
import {useGameStatus} from '../hooks';
import {formatMoney} from '../mechanics';
import NormalButton from './buttons/NormalButton';
import InfoBox from './InfoBox';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';

const MESSAGES = {
  success:
    'Well done! You achieved your goal. There is still room for improvement though and as everyone knows, practice makes perfect!',
  failure:
    'Bad luck! You made some mistakes and missed your target. Remember there is always help available, you only have to ask! Time to try again!',
};

type Props = {
  onAddScore: () => void;
  onPlayAgain: () => void;
};

const End = ({onAddScore, onPlayAgain}: Props): ReactElement => {
  const environment = useEnvironment();
  const dispatch = useAppDispatch();
  const {trackEvent} = useMatomo();
  const gameStatus = useGameStatus();
  // Use a ref to ensure end of game is only tracked once
  const endOfGameTrackedRef = useRef<boolean>(gameStatus.endOfGameTracked ?? false);
  const {isMobile} = useBreakpoint('mobile');

  const productionGoalCompleted = useMemo(
    () => gameStatus.totalProduction >= gameStatus.productionGoal,
    [gameStatus.productionGoal, gameStatus.totalProduction]
  );

  const moneyGoalCompleted = useMemo(
    () => gameStatus.money > gameStatus.initialMoney,
    [gameStatus.money, gameStatus.initialMoney]
  );

  const feedbackButton = useMemo(
    () => (
      <Link key={'feedback'} href={environment?.game.feedbackFormUrl ?? ''} target="_blank" rel="noopener noreferrer">
        <NormalButton>Provide game feedback</NormalButton>
      </Link>
    ),
    [environment?.game.feedbackFormUrl]
  );

  const playAgainButton = useMemo(
    () => (
      <NormalButton
        key="play-again"
        right={<RestartIcon className="h-sm w-sm" />}
        onClick={onPlayAgain}
        className={'px-lg mobile:px-xs'}
      >
        Play again
      </NormalButton>
    ),
    [onPlayAgain]
  );

  const addScoreButton = useMemo(
    () => (
      <NormalButton key={'add-score'} onClick={onAddScore}>
        Add score to leaderboard
      </NormalButton>
    ),
    [onAddScore]
  );

  const gameSuccess = productionGoalCompleted && moneyGoalCompleted;

  useEffect(() => {
    if (!endOfGameTrackedRef.current && !gameStatus.endOfGameTracked) {
      endOfGameTrackedRef.current = true;
      const action = gameSuccess ? 'game-success' : 'game-fail';
      const name = `{production: ${formatNumber(gameStatus.totalProduction)}, money: ${gameStatus.money}}`;
      trackEvent({category: 'game', action, name});
      dispatch(Actions.setEndOfGameTracked(true));
    }
  }, [dispatch, gameStatus.endOfGameTracked, gameStatus.money, gameStatus.totalProduction, gameSuccess, trackEvent]);

  const buttons = gameSuccess
    ? gameStatus.scoreAdded
      ? [playAgainButton]
      : [playAgainButton, gameStatus.scoreAdded ? null : addScoreButton]
    : [feedbackButton, playAgainButton];

  return (
    <MessageBox
      top={<SimplePill>End of game</SimplePill>}
      bottom={
        <Row
          className={classNames(
            'mx-2xl mobile:mx-md gap-sm',
            buttons.length === 1 ? 'justify-center' : 'justify-between'
          )}
        >
          {buttons}
        </Row>
      }
      dark
    >
      <Col className="space-y-sm text-center pt-xs px-32 mobile:px-6 mobile:pb-4">
        {!gameSuccess ? <LossIcon className="h-40 w-40 m-auto" /> : <CelebrationIcon className="h-40 w-40 m-auto" />}
        <p className="text-3xl font-semibold mobile:text-xl">{gameSuccess ? MESSAGES.success : MESSAGES.failure}</p>
        <InfoBox className={classNames('text-start text-2xl font-semibold !bg-primary-650 mobile:text-lg')}>
          <Col className="space-y-sm mobile:space-y-[4rem] mobile:pt-8 mobile:pb-4">
            <div
              className={classNames(
                'flex items-center',
                isMobile ? 'flex-col space-y-sm text-center' : 'flex-row space-x-md'
              )}
            >
              <Col>
                <ProductionIcon className="h-28 w-28" />
              </Col>
              <Col>
                <div>
                  Your land {!productionGoalCompleted && 'only '}produced{' '}
                  <span className={classNames(!productionGoalCompleted ? 'text-red-200' : 'text-primary-500')}>
                    {gameStatus.totalProduction.toFixed(2)} tonnes
                  </span>
                  .
                </div>
                <div>
                  You {productionGoalCompleted ? 'completed' : "didn't reach"} your goal of{' '}
                  <span className="text-primary-500">{gameStatus.productionGoal} tonnes</span>.
                </div>
              </Col>
            </div>
            <div
              className={classNames(
                'flex items-center',
                isMobile ? 'flex-col space-y-sm text-center' : 'flex-row space-x-md'
              )}
            >
              <Col>
                <MoneyIcon className="h-28 w-28" />
              </Col>
              <Col>
                <div>
                  You end up with{' '}
                  <span className={classNames(!moneyGoalCompleted && 'text-red-200', 'whitespace-nowrap')}>
                    {formatMoney(gameStatus.money)}
                  </span>
                  {moneyGoalCompleted
                    ? ', making a profit of '
                    : `, you were not able to recover the start up capital of `}
                  <span className={classNames(moneyGoalCompleted && 'text-primary-500', 'whitespace-nowrap')}>
                    {formatMoney(
                      moneyGoalCompleted ? gameStatus.money - gameStatus.initialMoney : gameStatus.initialMoney
                    )}
                  </span>
                  .
                </div>
              </Col>
            </div>
          </Col>
        </InfoBox>
      </Col>
    </MessageBox>
  );
};

export default End;
